/**
 * Custom hook for lazy loading rails progressively as the user scrolls
 *
 * @remarks
 * This hook implements progressive rail loading to improve initial page load performance.
 * Instead of loading all rails at once, it loads them in batches as the user scrolls.
 *
 * This is a specialized wrapper around useBatchedLoader for browse page rails.
 * It caches rail metadata on first load and progressively fetches tile data.
 *
 * @example
 * Basic usage
 * ```typescript
 * const { rails, loading, error, hasMore, loadTriggerRef } = useLazyLoadRails({
 *   slug: "/home",
 *   batchSize: 3,
 *   fetchTileDetails: true
 * });
 * ```
 *
 * @example
 * With custom scroll container and configuration
 * ```typescript
 * const scrollRef = useRef<HTMLDivElement>(null);
 * const { rails, loadTriggerRef, allRailsMetadata } = useLazyLoadRails({
 *   slug: "/movies",
 *   batchSize: 5,
 *   initialBatchSize: 7,
 *   loadThreshold: 1000,
 *   fetchTileDetails: true,
 *   skipPersonalized: true,
 *   scrollContainerRef: scrollRef,
 * });
 * ```
 */

import { useCallback, useRef } from "react";

import { UseLazyLoadRailsOptions, UseLazyLoadRailsResult } from "./types";

import { useBatchedLoader } from "@/utils/useBatchedLoader";
import { fetchBrowsePageBatched } from "@/utils/FetchBrowsePage/batched";
import { RailWithTiles } from "@/utils/FetchBrowsePage/types";

const useLazyLoadRails = ({
  slug,
  batchSize = 3,
  initialBatchSize,
  loadThreshold = 200,
  fetchTileDetails = false,
  skipPersonalized = true,
  showMobileContent = true,
  scrollContainerRef,
}: UseLazyLoadRailsOptions): UseLazyLoadRailsResult => {
  /** Cache all rails metadata to avoid refetching on every batch */
  const allRailsCache = useRef<RailWithTiles[] | null>(null);

  /** Create a stable fetch function for the generic loader */
  const fetchRailBatch = useCallback(
    async (startIndex: number, count: number) => {
      const result = await fetchBrowsePageBatched(slug, {
        fetchTileDetails,
        skipPersonalized,
        showMobileContent,
        startIndex,
        batchSize: count,
        allRails: allRailsCache.current || undefined,
      });

      /** Cache the metadata for subsequent calls */
      if (!allRailsCache.current) {
        allRailsCache.current = result.allRails;
      }

      return {
        items: result.loadedRails,
        hasMore: result.hasMore,
      };
    },
    [slug, fetchTileDetails, skipPersonalized, showMobileContent],
  );

  /** Use the generic batched loader */
  const {
    items: rails,
    loading,
    error,
    hasMore,
    loadTriggerRef,
    loadMore,
  } = useBatchedLoader<RailWithTiles>({
    fetchBatch: fetchRailBatch,
    batchSize,
    initialBatchSize,
    loadThreshold,
    resetKey: slug /** Reset when slug changes */,
    scrollContainerRef,
  });

  return {
    rails,
    loading,
    error,
    hasMore,
    loadTriggerRef,
    loadMore,
    allRailsMetadata: allRailsCache.current || [],
  };
};

export { useLazyLoadRails };
export type { UseLazyLoadRailsOptions, UseLazyLoadRailsResult } from "./types";
